import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

interface IncomingCallDialogProps {
  title: string;
  description: string;
  initialFocusRef: React.RefObject<HTMLButtonElement>;
  children: React.ReactNode;
  className?: string;
}

/**
 * A real modal layer is required here because an incoming call can arrive while
 * another Radix dialog (notably a pulse editor) already owns the body pointer
 * lock and focus scope.
 */
export function IncomingCallDialog({
  title,
  description,
  initialFocusRef,
  children,
  className,
}: IncomingCallDialogProps) {
  const [restoreFocusTarget] = React.useState<HTMLElement | null>(() => (
    typeof document === 'undefined' ? null : document.activeElement as HTMLElement | null
  ));

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[11000] pointer-events-auto bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="incoming-call-dialog"
          className={cn(
            'fixed left-1/2 top-1/2 z-[11001] pointer-events-auto w-[calc(100%-2rem)] max-w-sm max-h-[calc(100dvh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-card p-4 shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:p-6',
            className,
          )}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            initialFocusRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (restoreFocusTarget?.isConnected) restoreFocusTarget.focus();
          }}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}